import { Injectable } from "@angular/core";
import { OEDataModel } from "../../data/models/oe-data.model";
import { OEStrategyModel } from "../../data/models/oe-strategy.model";
import { OrderState, RequestOrderType, StrategyType } from "shared/cubiq-model-multi-asset";
import { ICAutocompleteGenericModel } from "../../interface/controls/models/bfx-interface-controls.models";
import { OEPanelGammaViewModel } from "app/order-entry/data/models/oe-panel-gamma-view.model";
import LoggerService from "@bfx/logger";

const logger = LoggerService.for(__dirname);

/* Should not have any dependencies */

@Injectable()
export class OEModelMasterService {
  /**********************************************************************/
  /* DATA */
  protected dataMap: Map<string, OEDataModel> = new Map();
  /**********************************************************************/
  /* CONSTRUCTOR*/
  constructor() {}

  /**********************************************************************/
  /* BASIC MODEL API */

  /* SINGLE MODEL GETTERS */
  public instance(refId: string): OEDataModel {
    if (this.has(refId)) {
      return this.get(refId);
    } else {
      throw new ReferenceError(`OEModel ['${refId}'] not found.`);
    }
  }

  public get(refId: string): OEDataModel | null {
    return this.has(refId) ? this.dataMap.get(refId) : null;
  }

  /* ******************************************** */
  /* GET ALL  */
  public getAll(): Map<string, OEDataModel> {
    return this.dataMap;
  }

  public getAllRefs(): Array<string> {
    return [...this.dataMap.keys()]; // also: Array.from(this.dataMap.keys());
  }

  public getAllRootRefs(): Array<string> {
    return this.getAllRefs().filter((refId) => this.isRootNode(refId));
  }

  /* ******************************************** */
  /* SETTERS  */
  public set(refId: string, model: OEDataModel): void {
    if (typeof refId === "string" && model instanceof OEDataModel && !this.has(refId)) {
      logger.out.info(`omm-s - Adding new model for ref Id ${refId}`);
      this.dataMap.set(refId, model);
    } else {
      throw new ReferenceError(`OEModel [${refId}] already exists.`);
    }
  }

  public update(refId: string, model: OEDataModel): void {
    this.dataMap.set(refId, model);
  }

  public reset(refId: string): void {
    if (this.has(refId)) {
      this.dataMap.get(refId).resetDataModel();
    } else {
      throw new ReferenceError(`OEModel ['${refId}'] not found.`);
    }
  }

  public delete(refId: string): void {
    if (this.has(refId)) {
      logger.out.info(`omm-s - Deleting model for ref Id ${refId}`);
      this.dataMap.delete(refId);
    }
  }

  /* ******************************************** */
  /* VALIDATION API - PUBLIC*/

  public has(refId: string): boolean {
    return this.validateModelWithRefId(refId);
  }

  public hasStrategy(refId: string): boolean {
    return this.validateModelWithRefIdForStrategy(refId);
  }

  public isSavedOrder(refId: string): boolean {
    let model: OEDataModel = this.dataMap.get(refId);
    return model != null && model.srvOrderState == OrderState.SAVED;
  }

  public isBenchmarkRequest(refId: string) {
    let model: OEDataModel = this.dataMap.get(refId);
    return (
      model instanceof OEDataModel &&
      model.orderType instanceof ICAutocompleteGenericModel &&
      (model.orderType.value == RequestOrderType.BMARK ||
        model.orderType.value == RequestOrderType.BMALGO)
    );
  }

  public isALGORequest(refId: string) {
    let model: OEDataModel = this.dataMap.get(refId);
    return (
      model instanceof OEDataModel &&
      model.orderType instanceof ICAutocompleteGenericModel &&
      model.orderType.value == RequestOrderType.ALGO
    );
  }

  public validateRefFormat(refId: string): boolean {
    if (refId != null && typeof refId === "string" && refId.length >= 4) {
      return true;
    } else {
      return false;
    }
  }

  /* VALIDATION API - PRIVATE*/
  private validateModelWithRefId(refId: string): boolean {
    if (
      this.validateRefFormat(refId) &&
      this.dataMap.has(refId) &&
      this.dataMap.get(refId) instanceof OEDataModel
    ) {
      return true;
    }
    return false;
  }

  private validateModelWithRefIdForStrategy(refId: string): boolean {
    if (this.validateModelWithRefId(refId)) {
      try {
        let model = this.dataMap.get(refId);
        return (
          (model != null &&
            model.strategy != null &&
            model.strategy instanceof OEStrategyModel &&
            ((model.strategy.parentRefId != null && model.strategy.parentRefId !== refId) ||
              (model.strategy.rootRefId != null && model.strategy.rootRefId !== refId) ||
              (model.strategy.children instanceof Array && model.strategy.children.length > 0) ||
              (model.strategy.siblings instanceof Array && model.strategy.siblings.length > 0))) ||
          model.strategy.strategyType.valueOf() == 1
        );
      } catch (ex) {
        return false;
      }
    }
    return false;
  }

  /* ******************************************** */
  /* STRATEGIES */
  public isRootNode(refId: string): boolean {
    if (this.hasStrategy(refId)) {
      return this.dataMap.get(refId).strategy.isRoot;
    } else {
      return this.has(refId); // model is valid but has no strategy therefore root
    }
  }

  public isGamma(refId: string): boolean {
    return (
      this.dataMap.get(refId) != null &&
      this.dataMap.get(refId).strategy != null &&
      this.dataMap.get(refId).strategy.strategyType == StrategyType.GAMMA
    );
  }

  public setGammaModel(refId: string, gammaLadderModel: OEPanelGammaViewModel) {}
}
